import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import cx from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';

import { ROUTE, TEXT, VALID } from 'common/const';
import { getBaseUrl, track } from 'common/utils';
import {
  Alert,
  Checkbox,
  Error,
  Form,
  Input,
  SubmitButton,
  TextLink,
} from 'common/components';
import { Page } from 'common/site';
import { requestOtp, verifyOtp } from 'data/customers';
import { useAuth } from 'app/context';
import { useAppInfo, useSearchParams } from 'common/hooks';

import styles from './Login.module.css';

const EMAIL = 'emailAddress';
const REMEMBER = 'rememberEmail';
const baseUrl = getBaseUrl(2);

const STEP = {
  REQUEST: 'request',
  VERIFY: 'verify',
};

// e.g. 'john@example.com' -> 'j•••n@example.com'
const maskEmail = (email = '') => {
  const [local, domain] = email.split('@');
  return domain ? `${local[0]}••••${local[local.length - 1]}@${domain}` : email;
};

const Login = () => {
  const [step, setStep] = useState(STEP.REQUEST);
  const [email, setEmail] = useState();
  const [resendCount, setResendCount] = useState(0);
  const [recaptchaValidated, setRecaptchaValidated] = useState(true);
  const recaptcha = useRef();
  const { isAuthenticated, login } = useAuth();
  const history = useHistory();

  const {
    mutate: submitRequestOtp,
    error: requestError,
    isLoading: isRequesting,
    reset: resetRequestOtp,
  } = useMutation(requestOtp, {
    onSuccess: () => setStep(STEP.VERIFY),
  });

  const {
    mutate: submitVerifyOtp,
    error: verifyError,
    isLoading: isVerifying,
    reset: resetVerifyOtp,
  } = useMutation(verifyOtp, {
    onSuccess: (data) => {
      track('login');
      login(data);
    },
  });

  const { data } = useAppInfo();

  // Get the query parameter for the redirect
  const nextRoute = useSearchParams().redirect ?? ROUTE.DASHBOARD;

  const remember = useMemo(
    () => sessionStorage.getItem(REMEMBER) !== 'false',
    [],
  );
  const rememberedEmail = useMemo(
    () => (remember ? localStorage.getItem(EMAIL) : undefined),
    [remember],
  );

  const handleRequestSubmit = async ({ email, remember }) => {
    const captchaValue = recaptcha.current.getValue();
    if (!captchaValue) {
      setRecaptchaValidated(false);
    } else {
      setRecaptchaValidated(true);

      const response = await axios.post(`${baseUrl}recaptcha/verify`, {
        captchaValue,
      });
      if (response.data.resultInformation.actionResult === 'Successful') {
        recaptcha.current.reset();
        sessionStorage.setItem(REMEMBER, remember);

        if (remember) {
          localStorage.setItem(EMAIL, email);
        } else {
          localStorage.removeItem(EMAIL);
        }

        setEmail(email);
        submitRequestOtp({ email });
      }
    }
  };

  const handleVerifySubmit = ({ otp }) => {
    submitVerifyOtp({ email, otp });
  };

  const handleResend = () => {
    if (isRequesting) return;

    resetVerifyOtp();
    submitRequestOtp(
      { email },
      // clear the previously entered passcode once the new one is sent
      { onSuccess: () => setResendCount((count) => count + 1) },
    );
  };

  // handle success (after context updates)
  useEffect(() => {
    if (isAuthenticated) {
      history.push(nextRoute);
    }
  }, [isAuthenticated, history, nextRoute]);

  return (
    <Page
      variant="island"
      title={
        step === STEP.REQUEST
          ? 'Log into Online Ordering'
          : 'Enter verification code'
      }
      subtitle={
        step === STEP.VERIFY
          ? `6-digit code sent to: ${maskEmail(email)}.`
          : undefined
      }
    >
      {data?.loginAlert && step === STEP.REQUEST && (
        <div className={styles.alert}>
          <Alert>{data.loginAlert}</Alert>
        </div>
      )}

      {step === STEP.REQUEST ? (
        <Form
          defaultValues={{ email: rememberedEmail }}
          validation={{
            email: VALID.EMAIL.required('Please enter your email.'),
          }}
          onPass={handleRequestSubmit}
          isSubmitting={isRequesting}
        >
          {requestError && (
            <div className={styles.error}>
              <Error>{requestError?.message}</Error>
            </div>
          )}

          <div className={styles.form}>
            <Input name="email" />
          </div>
          <div className={cx(TEXT.BODY_1, styles.group)}>
            <Checkbox
              name="remember"
              label="Save email"
              defaultChecked={remember}
            />
          </div>

          <ReCAPTCHA
            className={styles.recaptcha}
            ref={recaptcha}
            sitekey={process.env.REACT_APP_SITE_KEY}
          />

          {!recaptchaValidated && (
            <div className={styles.errorMessage}>
              Please verify that you are not a robot.
            </div>
          )}

          <SubmitButton
            className={styles.button}
            name="submit"
            fullWidth
            isSubmitting={isRequesting}
          >
            Login
          </SubmitButton>
          <p className={cx(TEXT.BODY_1, styles.footer)}>
            New to Online Ordering?{' '}
            <TextLink to={ROUTE.SIGNUP}>Sign Up</TextLink>
          </p>
        </Form>
      ) : (
        <>
          <Form
            key={`verify-${resendCount}`}
            validation={{
              otp: VALID.OTP.required('Please enter the code we emailed you.'),
            }}
            onPass={handleVerifySubmit}
            isSubmitting={isVerifying}
          >
            {requestError && (
              <div className={styles.error}>
                <Error>{requestError?.message}</Error>
              </div>
            )}
            {verifyError && (
              <div className={styles.error}>
                <Error>{verifyError?.message}</Error>
              </div>
            )}

            <div className={styles.form}>
              <Input
                name="otp"
                label="Passcode"
                number
                maxlength={6}
                autoComplete="one-time-code"
              />
            </div>

            <SubmitButton
              className={styles.button}
              name="submit"
              fullWidth
              isSubmitting={isVerifying}
            >
              Verify Code
            </SubmitButton>
          </Form>

          <SubmitButton
            className={styles.button}
            type="button"
            onClick={handleResend}
            fullWidth
            outlined
            isSubmitting={isRequesting}
          >
            Resend Code
          </SubmitButton>
        </>
      )}
    </Page>
  );
};

export default Login;
